import React, { useState } from "react";
import PhoneRegister from "./PhoneRegister";
import "./RegisterCard.css";
import { useNavigate } from "react-router-dom";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import { generateFullFingerprintPayload } from "../Tools/fingerprint";
import { hasSecurityFingerprintConsent } from "../Tools/fingerprintConsent";
import { useLanguage } from "../i18n";

export default function RegisterCard({ onClose }) {
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Registration and authentication states
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showPhoneRegister, setShowPhoneRegister] = useState(false);
  const [showGoogle, setShowGoogle] = useState(false);
  const [showApple, setShowApple] = useState(false);

  // Email registration form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [emailStep, setEmailStep] = useState("details");
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [emailMessage, setEmailMessage] = useState("");

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const passwordValid = password.length >= 8 && password.trim().length > 0;
  const confirmPasswordValid =
    confirmPassword.length > 0 && confirmPassword === password;

  const emailFormValid =
    emailValid && passwordValid && confirmPasswordValid;

  /*
   * =========================================
   * EMAIL REGISTRATION
   * =========================================
   *
   * Create the account, request its verification code, and
   * then let the user finish verification below.
   */
  const handleRegisterEmail = async () => {
    if (!emailFormValid) return;

    setEmailLoading(true);
    setEmailError("");
    setEmailMessage("");

    try {
      const fingerprint = hasSecurityFingerprintConsent()
        ? await generateFullFingerprintPayload()
        : null;

      const response = await fetch("/api/auth/email/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          password,
          fingerprint,
          deviceRecognitionConsent: hasSecurityFingerprintConsent()
        })
      });

      const data = await response.json();

      if (!response.ok || !data.ok) {
        throw new Error(data.error || "Unable to create your account.");
      }

      setEmailStep("verification");
      setEmailMessage(t("emailSent", { email: email.trim().toLowerCase() }));
    } catch (error) {
      console.error("Email registration error:", error);
      setEmailError(t(error.message === "Email address already registered" ? "emailExists" : error.message === "Invalid email address" ? "invalidEmail" : error.message?.startsWith("Password must contain") ? "passwordTooShort" : "emailSendError"));
    } finally {
      setEmailLoading(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (!/^\d{6}$/.test(emailCode.trim())) {
      setEmailError(t("codeRequired"));
      return;
    }

    setEmailLoading(true);
    setEmailError("");
    setEmailMessage("");

    try {
      const response = await fetch("/api/auth/email/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          code: emailCode.trim()
        })
      });
      const data = await response.json();

      if (!response.ok || !data.ok) {
        throw new Error(data.error || "Unable to verify your email.");
      }

      window.dispatchEvent(new Event("bubly:session-changed"));
      navigate("/profile");
    } catch (error) {
      console.error("Email verification error:", error);
      setEmailError(t(error.message === "Invalid or expired verification code" ? "invalidCode" : "emailVerifyError"));
    } finally {
      setEmailLoading(false);
    }
  };

  /*
   * =========================================
   * GOOGLE REGISTRATION CARD
   * =========================================
   *
   * The Google authentication flow remains
   * functionally unchanged.
   */
  if (showGoogle) {
    return (
      <div className="google-card">

        {/* Close Google registration card */}
        <button
          className="popup-close"
          onClick={() => setShowGoogle(false)}
            aria-label={t("close")}
        >
          ✕
        </button>

        {/* BublyUp branding */}
        <div className="google-branding">
          <img
            src="/assets/img/LogoBubly.png"
            alt="BublyUp"
            className="google-bublyup-logo"
          />
        </div>

        {/* Google authentication title */}
        <h2 className="popup-title">{t("googleTitle")}</h2>

        {/* Google authentication description */}
        <p className="google-message">
          {t("googleDescription")}
        </p>

        {/* Google authentication component */}
        <div className="google-wrapper">
          <GoogleOAuthProvider clientId="801390879975-n7n4ji4c9nfhr1q8bfm9reste21tn4ci.apps.googleusercontent.com">
          <GoogleLogin
            onSuccess={async (credentialResponse) => {
              try {
                const token = credentialResponse.credential;

                if (!token) {
                  console.error("Google credential is missing.");
                  return;
                }

                const fingerprint = hasSecurityFingerprintConsent()
                  ? await generateFullFingerprintPayload()
                  : null;

                const res = await fetch(
                  "/api/auth/google",
                  {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json"
                    },
                    credentials: "include",
                    body: JSON.stringify({
                      token,
                      fingerprint,
                      deviceRecognitionConsent: hasSecurityFingerprintConsent()
                    })
                  }
                );

                const data = await res.json();

                console.log(
                  "Google backend response:",
                  data
                );

                if (data.ok) {
                  window.dispatchEvent(new Event("bubly:session-changed"));
                  navigate("/profile");
                } else {
                  console.error(
                    "Google backend error:",
                    data.error
                  );
                }
              } catch (err) {
                console.error(
                  "Google authentication error:",
                  err
                );
              }
            }}

            onError={() => {
              console.error("Google Login error.");
            }}
          />
          </GoogleOAuthProvider>
        </div>

        {/* Google brand logo */}
        <div className="google-logo-below">
          <img
            src="/assets/img/LogoGoogle.png"
            alt="Google"
            className="google-g-logo"
          />
        </div>

        {/* Security information */}
        <div className="google-security">
          <div className="google-security-line" />

          <p>
            {t("secureAuth")}
          </p>

          <div className="google-security-line" />
        </div>

        <p className="google-security-text">
          {t("googleSecurity")}
        </p>
      </div>
    );
  }

  /*
   * =========================================
   * MAIN REGISTRATION CARD
   * =========================================
   */

  return (
    <div className="register-card">

      {/* Main card close button */}
      <button
        className="close-btn"
        onClick={onClose}
        aria-label={t("close")}
      >
        ✕
      </button>

      {/* =====================================
          AUTHENTICATION METHOD SELECTION
          ===================================== */}

      {!showEmailForm && !showPhoneRegister && (
        <>
        

          <h2 className="rc-title">{t("createYourAccount")}</h2>



          <p className="rc-legal">
            {t("agreeToTerms")}
            <br />
            {t("privacyCookies")}
          </p>

          <div className="rc-register-logo">
  <img
    src="/assets/img/LogoBubddly.png"
    alt="BublyUp"
  />
</div>

          

          <div className="rc-buttons">

            {/* Phone registration */}
            <button
              type="button"
              className="rc-btn rc-phone"
              onClick={() => setShowPhoneRegister(true)}
            >
              <span className="rc-btn-icon">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M6.6 3.5 9.1 3c.7-.1 1.3.3 1.5.9l1.1 3c.2.5 0 .9-.4 1.2L9.7 9.4c1 2.1 2.7 3.9 4.9 4.9l1.3-1.6c.3-.4.8-.6 1.2-.4l3 1.1c.6.2 1 .8.9 1.5l-.5 2.5c-.1.7-.7 1.1-1.4 1.1C11.4 18.5 5.5 12.6 5.5 4.9c0-.7.4-1.3 1.1-1.4Z"
                    fill="#666666"
                  />
                </svg>
              </span>

              <span>
                {t("phoneLogin")}
              </span>
            </button>

            {/* Google registration */}
            <button
              type="button"
              className="rc-btn rc-google"
              onClick={() => setShowGoogle(true)}
            >
              <span className="rc-btn-icon rc-google-icon">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 48 48"
                  aria-hidden="true"
                >
                  <path
                    fill="#EA4335"
                    d="M24 9.5c3.54 0 6.72 1.22 9.22 3.6l6.84-6.84C35.9 2.4 30.47 0 24 0 14.61 0 6.51 5.38 2.56 13.22l7.97 6.19C12.43 13.31 17.74 9.5 24 9.5Z"
                  />
                  <path
                    fill="#4285F4"
                    d="M46.5 24.5c0-1.59-.14-3.13-.41-4.62H24v9.02h12.62c-.54 2.91-2.18 5.38-4.65 7.03l7.52 5.83C43.87 37.63 46.5 31.56 46.5 24.5Z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M10.53 28.59A14.5 14.5 0 0 1 9.5 24c0-1.59.36-3.12 1.03-4.59l-7.97-6.19A23.96 23.96 0 0 0 0 24c0 3.88.93 7.56 2.56 10.78l7.97-6.19Z"
                  />
                  <path
                    fill="#34A853"
                    d="M24 48c6.48 0 11.91-2.14 15.87-5.83l-7.52-5.83c-2.08 1.39-4.74 2.21-8.35 2.21-6.26 0-11.57-3.81-13.47-9.19l-7.97 6.19C6.51 42.62 14.61 48 24 48Z"
                  />
                </svg>
              </span>

              <span>
                {t("googleContinue")}
              </span>
            </button>

            {/* Email registration */}
            <button
              type="button"
              className="rc-btn rc-email"
              onClick={() => setShowEmailForm(true)}
            >
              <span className="rc-btn-icon">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <rect
                    x="3"
                    y="5"
                    width="18"
                    height="14"
                    rx="3"
                    stroke="#666666"
                    strokeWidth="1.8"
                  />
                  <path
                    d="m5 7 7 5.5L19 7"
                    stroke="#666666"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>

              <span>
                {t("emailLogin")}
              </span>
            </button>

            {/* Apple registration */}
            <button
              type="button"
              className="rc-btn rc-apple"
              onClick={() => setShowApple(true)}
            >
              <span className="rc-btn-icon rc-apple-icon">
             <svg
  width="35"
  height="35"
  viewBox="3 2 18 20"
  aria-hidden="true"
>
  <path
    fill="#111111"
    d="M17.05 12.54c.02-2.1 1.72-3.11 1.8-3.16a3.85 3.85 0 0 0-3.03-1.64c-1.28-.13-2.52.76-3.18.76-.66 0-1.68-.74-2.76-.72a4.07 4.07 0 0 0-3.42 2.09c-1.47 2.55-.37 6.3 1.05 8.37.7 1.01 1.51 2.14 2.59 2.1 1.04-.04 1.43-.67 2.69-.67 1.25 0 1.61.67 2.7.65 1.12-.02 1.82-1.02 2.5-2.04.79-1.15 1.11-2.26 1.13-2.32-.02-.01-2.15-.83-2.17-3.42Zm-2.08-6.16c.57-.69.95-1.64.85-2.59-.82.03-1.81.55-2.4 1.24-.52.6-.98 1.57-.86 2.5.92.07 1.85-.47 2.41-1.15Z"
  />
</svg>
              </span>

              <span>
                {t("appleContinue")}
              </span>
            </button>

          </div>
        </>
      )}

      {/* =====================================
          EMAIL REGISTRATION FORM
          ===================================== */}

      {showEmailForm && (
        <>
          <h2 className="rc-title">
            {emailStep === "details" ? t("createAccount") : t("verifyEmail")}
          </h2>

          {emailStep === "details" ? <>
            <div className="rc-email-form">

            <div className="rc-input-wrap">
              <input
                type="email"
                placeholder={t("email")}
                className="rc-input rc-input-validated"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
              {email.length > 0 && (
                <span
                  className={`rc-validation-indicator ${
                    emailValid ? "is-valid" : "is-invalid"
                  }`}
                  aria-hidden="true"
                >
                  {emailValid ? "✓" : "×"}
                </span>
              )}
            </div>

            <div className="rc-input-wrap">
              <input
                type="password"
                placeholder={t("password")}
                className="rc-input rc-input-validated"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
              />
              {password.length > 0 && (
                <span
                  className={`rc-validation-indicator ${
                    passwordValid ? "is-valid" : "is-invalid"
                  }`}
                  aria-hidden="true"
                >
                  {passwordValid ? "✓" : "×"}
                </span>
              )}
            </div>

            <div className="rc-input-wrap">
              <input
                type="password"
                placeholder={t("confirmPassword")}
                className="rc-input rc-input-validated"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
              />
              {confirmPassword.length > 0 && (
                <span
                  className={`rc-validation-indicator ${
                    confirmPasswordValid ? "is-valid" : "is-invalid"
                  }`}
                  aria-hidden="true"
                >
                  {confirmPasswordValid ? "✓" : "×"}
                </span>
              )}
            </div>

            </div>

            {emailError && <p className="rc-form-message rc-form-error" role="alert">{emailError}</p>}
            <button
              type="button"
              className="rc-btn rc-email"
              onClick={handleRegisterEmail}
              disabled={!emailFormValid || emailLoading}
            >
              {emailLoading ? t("sendingCode") : t("continue")}
            </button>
          </> : <>
            <p className="rc-email-help" role="status">{emailMessage}</p>
            <div className="rc-email-form">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder={t("codePlaceholder")}
                className="rc-input"
                value={emailCode}
                onChange={(event) => setEmailCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                disabled={emailLoading}
                aria-label={t("emailCodeLabel")}
              />
            </div>
            {emailError && <p className="rc-form-message rc-form-error" role="alert">{emailError}</p>}
            <button
              type="button"
              className="rc-btn rc-email"
              onClick={handleVerifyEmail}
              disabled={emailCode.length !== 6 || emailLoading}
            >
              {emailLoading ? t("verifying") : t("verifyEmail")}
            </button>
            <button
              type="button"
              className="rc-btn rc-email"
              onClick={() => { setEmailStep("details"); setEmailCode(""); setEmailError(""); }}
              disabled={emailLoading}
            >
              {t("back")}
            </button>
          </>}

          {emailStep === "details" && <button
            type="button"
            className="rc-btn rc-email"
            onClick={() => { setShowEmailForm(false); setEmailError(""); }}
            disabled={emailLoading}
          >{t("back")}</button>}
        </>
      )}

      {/* =====================================
          PHONE REGISTRATION
          ===================================== */}

      {showPhoneRegister && (
        <PhoneRegister
          onClose={() => setShowPhoneRegister(false)}
          onOTP={() => {
            window.dispatchEvent(new Event("bubly:session-changed"));
            navigate("/profile");
          }}
        />
      )}

      {/* =====================================
          MOBILE APP DOWNLOAD SECTION
          ===================================== */}

      {!showEmailForm && !showPhoneRegister && (
        <div className="rc-app-section">

          <p>{t("getApp")}</p>

          {/* Official app store buttons */}
          <div className="rc-store-buttons">

            <img
              src="/assets/img/Apple_Store.png"
              alt={t("downloadApple")}
            />

            <img
              src="/assets/img/Playstore.png"
              alt={t("downloadGoogle")}
            />

          </div>

          {/* Android APK download */}
          <a
            href="/downloads/bublyup.apk"
            className="rc-apk-link"
            download
          >
            <span className="rc-apk-icon">
              🤖
            </span>

            <span>
              {t("downloadAndroid")}
            </span>
          </a>

        </div>
      )}

    </div>
  );
}
